import Cookies from "js-cookie";
import { Utils } from "@/src/Utils";
import { PublicSettings } from "@/src/config/PublicSettings";
import { BuilderTestUtils } from "@/src/utils/BuilderTestUtils";

const PERMANENT_TRACKING_PARAM_NAMES = ["pcode", "PCODE", "pCode", "adref", "clickref", "aff_id", "rsCode", "rsEngagementMedium", "rsShareMedium"];

export class TrackingUtils {
    private static getCookieDomain(): string | undefined {
        if (typeof window === "undefined") {
            return PublicSettings.COOKIE_DOMAIN;
        }

        const hostname = window.location.hostname;

        if (hostname.includes(".amplifyapp.com")) {
            return undefined;
        }

        return PublicSettings.COOKIE_DOMAIN;
    }

    private static isSecureContext(): boolean {
        if (typeof window === "undefined") {
            return !PublicSettings.COOKIE_DOMAIN.includes("localhost");
        }

        const hostname = window.location.hostname;

        if (hostname.includes("localhost") || hostname === "127.0.0.1") {
            return false;
        }

        return true;
    }

    static initializeTrackingParams() {
        const queryParams = Utils.getQueryParameters();

        TrackingUtils.setSessionTrackingParams(queryParams);

        const existingPersistentQueryParams = TrackingUtils.getPersistentTrackingParams();

        for (const paramName of PERMANENT_TRACKING_PARAM_NAMES) {
            if (paramName in queryParams) {
                existingPersistentQueryParams[paramName] = queryParams[paramName];
            }
        }

        TrackingUtils.setPersistentTrackingParams(existingPersistentQueryParams);
    }

    static setPersistentTrackingParams(params: Record<string, any>) {
        const paramsString = JSON.stringify(params);
        try {
            localStorage.setItem("trackingParams", paramsString);
        } catch (e) {
            console.log("Unable to setPersistentTrackingParams in localstorage", e);
        }

        try {
            const cookieOptions: Cookies.CookieAttributes = {
                path: "/",
                secure: TrackingUtils.isSecureContext(),
                expires: 365,
                sameSite: "Lax"
            };

            const domain = TrackingUtils.getCookieDomain();
            if (domain) {
                cookieOptions.domain = domain;
            }

            Cookies.set("trackingParams", paramsString, cookieOptions);
        } catch (e) {
            console.log("Unable to setPersistentTrackingParams in cookie", e);
        }
    }

    static getPersistentTrackingParams(): Record<string, any> {
        let sessionStorageParams = {};
        let cookieParams = {};

        try {
            sessionStorageParams = JSON.parse(localStorage.getItem("trackingParams") ?? "{}");
        } catch (e) {
            console.log("Unable to getPersistentTrackingParams in localStorage", e);
        }

        try {
            cookieParams = JSON.parse(Cookies.get("trackingParams") ?? "{}");
        } catch (e) {
            console.log("Unable to getPersistentTrackingParams in cookie", e);
        }

        return {
            ...cookieParams,
            ...sessionStorageParams
        };
    }

    static setSessionTrackingParams(params: Record<string, any>) {
        const paramsString = JSON.stringify(params);
        try {
            sessionStorage.setItem("sessionTrackingParams", paramsString);
        } catch (e) {
            console.log("Unable to setSessionTrackingParams in sessionStorage", e);
        }

        try {
            const cookieOptions: Cookies.CookieAttributes = {
                path: "/",
                secure: TrackingUtils.isSecureContext(),
                sameSite: "Lax"
            };

            const domain = TrackingUtils.getCookieDomain();
            if (domain) {
                cookieOptions.domain = domain;
            }

            Cookies.set("sessionTrackingParams", paramsString, cookieOptions);
        } catch (e) {
            console.log("Unable to setSessionTrackingParams in cookie", e);
        }
    }

    static getSessionTrackingParams(): Record<string, any> {
        let sessionStorageParams = {};
        let cookieParams = {};

        try {
            sessionStorageParams = JSON.parse(sessionStorage.getItem("sessionTrackingParams") ?? "{}");
        } catch (e) {
            console.log("Unable to getSessionTrackingParams in sessionStorage", e);
        }

        try {
            cookieParams = JSON.parse(Cookies.get("sessionTrackingParams") ?? "{}");
        } catch (e) {
            console.log("Unable to getSessionTrackingParams in cookie", e);
        }

        return {
            ...cookieParams,
            ...sessionStorageParams
        };
    }

    static combineQueryParams() {
        const query = Utils.getQueryParameters();

        const sessionTrackingParams = TrackingUtils.getSessionTrackingParams();
        const persistentTrackingParams = TrackingUtils.getPersistentTrackingParams();

        return {
            ...persistentTrackingParams,
            ...sessionTrackingParams,
            ...query
        };
    }

    /**
     * Collects all tracking parameters that should be passed to the quote site
     * This mirrors the logic in middleware.ts handleQuoteRedirect
     * Used by the quote widget to ensure identical tracking behavior
     */
    static collectQuoteTrackingParams(): Record<string, string> {
        const trackingParams: Record<string, string> = {};

        // Get session and persistent tracking params using existing methods
        const sessionTrackingParams = TrackingUtils.getSessionTrackingParams();
        const persistentTrackingParams = TrackingUtils.getPersistentTrackingParams();

        // Combine them
        Object.assign(trackingParams, persistentTrackingParams, sessionTrackingParams);

        // Add current URL search params
        if (typeof window !== "undefined") {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.forEach((value, key) => {
                trackingParams[key] = value;
            });
        }

        // Add quoteId if present (check all locale variants)
        const quoteIdPtzUs = Cookies.get("quoteIdPtzUs");
        const quoteIdPtzCa = Cookies.get("quoteIdPtzCa");
        const quoteId = quoteIdPtzUs || quoteIdPtzCa;

        if (quoteId) {
            trackingParams.quoteId = quoteId;
        }

        // Add Builder session and visitor IDs
        const builderSessionId = Cookies.get("builderSessionId");
        if (builderSessionId) {
            trackingParams.builderSessionId = builderSessionId;
        }

        const builderVisitorId = Cookies.get("builderVisitorId");
        if (builderVisitorId) {
            trackingParams.builderVisitorId = builderVisitorId;
        }

        // Add Builder test variants as dynamic params
        const builderVariantParams = BuilderTestUtils.getBuilderTestParams();
        Object.assign(trackingParams, builderVariantParams);

        // Filter out empty values and tuid (matches middleware behavior)
        const filteredParams: Record<string, string> = {};
        for (const [key, value] of Object.entries(trackingParams)) {
            if (value !== "" && key !== "tuid") {
                filteredParams[key] = String(value);
            }
        }

        return filteredParams;
    }

    private static readonly MAX_RETRIES = 5;
    private static readonly RETRY_DELAY = 1000;
    private static segmentIdRetryPromise: Promise<string> | null = null;

    static sendDataDogAction(actionName: string, actionProperties?: Record<string, any>) {
        if (typeof window === "undefined") {
            return;
        }

        const attemptSend = (retryCount = 0) => {
            if (window.DD_RUM?.addAction) {
                try {
                    window.DD_RUM.addAction(actionName, actionProperties);
                } catch (e) {
                    console.warn("TrackingUtils: Failed to send DataDog action:", {
                        actionName,
                        error: e
                    });
                }
                return;
            }

            if (retryCount < this.MAX_RETRIES) {
                setTimeout(() => attemptSend(retryCount + 1), this.RETRY_DELAY);
            } else {
                console.warn("TrackingUtils: Failed to send DataDog action after max retries", {
                    actionName,
                    maxRetries: this.MAX_RETRIES,
                    hasDD_RUM: !!window.DD_RUM,
                    hasAddActionMethod: !!window.DD_RUM?.addAction,
                    properties: actionProperties
                });
            }
        };

        attemptSend();
    }

    static getSegmentAnonymousId(): Promise<string> {
        if (this.segmentIdRetryPromise) {
            return this.segmentIdRetryPromise;
        }

        this.segmentIdRetryPromise = new Promise<string>(resolve => {
            const attemptGet = (retryCount = 0) => {
                const anonId = Cookies.get("ajs_anonymous_id");

                if (anonId) {
                    resolve(anonId);
                    this.segmentIdRetryPromise = null;
                    return;
                }

                if (retryCount < this.MAX_RETRIES) {
                    setTimeout(() => attemptGet(retryCount + 1), this.RETRY_DELAY);
                } else {
                    console.warn("TrackingUtils: Failed to get segment anonymous ID after max retries");
                    const fallbackId = `anon_${Date.now()}`;
                    resolve(fallbackId);
                    this.segmentIdRetryPromise = null;
                }
            };

            attemptGet();
        });

        return this.segmentIdRetryPromise;
    }

    static async setDataDogUser(userAttributes: Record<string, any>) {
        if (typeof window === "undefined") {
            return;
        }

        try {
            const segmentAnonId = await this.getSegmentAnonymousId();

            const dataDogUser = {
                id: segmentAnonId,
                spotWebAttributes: {
                    ...userAttributes
                }
            };

            try {
                const stringifiedUser = JSON.stringify(dataDogUser);

                const cookieOptions: Cookies.CookieAttributes = {
                    path: "/",
                    secure: TrackingUtils.isSecureContext(),
                    sameSite: "Lax"
                };

                const domain = TrackingUtils.getCookieDomain();
                if (domain) {
                    cookieOptions.domain = domain;
                }

                Cookies.set("spotWebDataDogUser", stringifiedUser, cookieOptions);
            } catch (e) {
                console.log("Unable to set spotWebDataDogUser in cookie", e);
            }

            if (window.DD_RUM) {
                window.DD_RUM.onReady(() => {
                    window.DD_RUM?.setUser(dataDogUser);
                });
            }
        } catch (error) {
            console.warn("TrackingUtils: Error setting DataDog user:", error);
        }
    }
}
